import { LineComponentCard } from '@qr-front/icons/line-component-card'
import { LineFileText } from '@qr-front/icons/line-file-text'
import { LineGenerateImage } from '@qr-front/icons/line-generate-image'
import { LineLinkAlt01 } from '@qr-front/icons/line-link-alt-01'
import { Button } from '@qr-front/ui/components/Button'
import { Separator } from '@qr-front/ui/components/Separator'
import { ROUTES } from 'constants/routesConstants'
import { QR_TYPE } from 'features/generator/generatorTypes'
import { getQrTypeTranslation } from 'features/generator/utils/qrTypeData'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'

import { Link } from '@/i18n-routing'
import { TTranslationFunction } from '@/types'

import { TGeneratorType } from '../generator/components/QrTypeCard/QrTypeCard'
import {
	ErrorDetails,
	TErrorCodeToTranslations,
} from './components/ErrorDetails'
import { IllustrationText } from './components/IllustrationText'

const ErrorIllustration = dynamic(() =>
	import('./components/ErrorIllustration').then((m) => ({
		default: m.ErrorIllustration,
	})),
)
import { LazyQrTypeCard } from './components/LazyQrTypeCard'
import { TErrorPageHandledCode } from './components/useErrorCode'

const getGeneratorTypeList = (
	t: ReturnType<typeof useTranslations>,
): TGeneratorType[] => [
	{
		name: getQrTypeTranslation(t)(QR_TYPE.Url),
		description: t('generator__step_1__qr_type_cards__url__description'),
		icon: <LineLinkAlt01 />,
		url: ROUTES.generator.url,
	},
	{
		name: getQrTypeTranslation(t)(QR_TYPE.VCard),
		description: t('generator__step_1__qr_type_cards__vcard__description'),
		icon: <LineComponentCard />,
		url: ROUTES.generator.vCard,
	},
	{
		name: getQrTypeTranslation(t)(QR_TYPE.Pdf),
		description: t('generator__step_1__qr_type_cards__pdf__description'),
		icon: <LineFileText />,
		url: ROUTES.generator.pdf,
	},
	{
		name: getQrTypeTranslation(t)(QR_TYPE.Images),
		description: t('generator__step_1__qr_type_cards__images__description'),
		icon: <LineGenerateImage />,
		url: ROUTES.generator.images,
	},
]

const getErrorCodeToTranslations = (
	t: TTranslationFunction,
): TErrorCodeToTranslations => ({
	'404': {
		title: t('public__page__not_found_title'),
		description: t('public__page__not_found_description'),
	},
	'403': {
		title: t('public__page__forbidden_title'),
		description: t('public__page__forbidden_description'),
	},
	'500': {
		title: t('public__generic__unexpected_error_title'),
		description: t('public__generic__unexpected_error_subtitle'),
	},
})

interface ErrorPageContentProps {
	errorCode?: TErrorPageHandledCode
	customTitle?: string
	customDescription?: string
	hideQrSection?: boolean
}

export const ErrorPageContent = ({
	errorCode,
	customTitle,
	customDescription,
	hideQrSection = false,
}: ErrorPageContentProps = {}) => {
	const t = useTranslations()

	const errorCodeToTranslations = getErrorCodeToTranslations(t)
	const buttonText = t('public__page__not_found_button')
	const sectionTitle = t('public__page__not_found__qr_code__title')
	const generatorTypes = getGeneratorTypeList(t)

	return (
		<div className="flex flex-col items-center overflow-hidden px-5 pb-0 pt-20 desktop:pt-24">
			<div className="flex w-full flex-col items-center gap-10">
				<ErrorIllustration text={<IllustrationText code={errorCode} />} />
				<div className="flex w-full flex-col items-center gap-6 desktop:w-100">
					<div className="flex flex-col items-center gap-2">
						<ErrorDetails
							errorCodeToTranslations={errorCodeToTranslations}
							customTitle={customTitle}
							customDescription={customDescription}
						/>
					</div>
					<Button size="large" asChild>
						<Link href={ROUTES.index}>{buttonText}</Link>
					</Button>
				</div>
			</div>
			{!hideQrSection && (
				<div className="flex flex-col items-center gap-8 py-20 desktop:gap-12 desktop:py-24">
					<Separator collapse className="w-full" />
					<h3 className="type-heading-3 mt-12">{sectionTitle}</h3>
					<ul className="flex flex-col justify-center gap-4 desktop:flex-row desktop:gap-6">
						{generatorTypes.map((type) => (
							<li key={type.name}>
								<LazyQrTypeCard
									type={type}
									className="h-full w-[350px] gap-6 desktop:w-[286px]"
								/>
							</li>
						))}
					</ul>
				</div>
			)}
		</div>
	)
}
